/**
 * TrendBars.tsx — a hand-rolled SVG grouped-bar chart for a daily COUNT series
 * (2026-07-21). No chart library (bundle-lean house style, like
 * journey/ProjectionChart.tsx); colours are CSS variables so dark mode reskins
 * for free. Renders counts only — never money.
 */

export interface TrendSeries {
  label: string;
  /** A CSS colour (typically a var(--gps-*) token). */
  color: string;
  /** One value per x-position; must be the same length as `labels`. */
  values: number[];
}

const W = 720;
const H = 220;
const PAD_L = 28;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 28;

/** At most this many x-axis date labels (evenly spaced) to avoid clutter. */
const MAX_X_LABELS = 6;

export function TrendBars({
  labels,
  series,
  ariaLabel,
}: {
  labels: string[];
  series: TrendSeries[];
  ariaLabel: string;
}) {
  const n = labels.length;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const groupW = plotW / Math.max(1, n);
  const barGap = 1.5;
  const barW = Math.max(1, (groupW - barGap * (series.length + 1)) / series.length);
  const yFor = (v: number) => PAD_T + plotH * (1 - v / max);
  const labelStep = Math.max(1, Math.ceil(n / MAX_X_LABELS));

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }} role="img" aria-label={ariaLabel}>
        {/* Baseline. */}
        <line x1={PAD_L} y1={PAD_T + plotH} x2={W - PAD_R} y2={PAD_T + plotH} style={{ stroke: 'var(--gps-border)' }} strokeWidth="1" />
        {/* Max gridline + label. */}
        <line x1={PAD_L} y1={PAD_T} x2={W - PAD_R} y2={PAD_T} style={{ stroke: 'var(--gps-border)' }} strokeWidth="1" strokeDasharray="2 4" />
        <text x={0} y={PAD_T + 4} fontSize="10" style={{ fill: 'var(--gps-muted)' }}>{max}</text>
        <text x={0} y={PAD_T + plotH} fontSize="10" style={{ fill: 'var(--gps-muted)' }}>0</text>

        {labels.map((label, i) => {
          const x0 = PAD_L + i * groupW;
          return (
            <g key={label}>
              {series.map((s, j) => {
                const v = s.values[i] ?? 0;
                const y = yFor(v);
                return (
                  <rect
                    key={s.label}
                    x={x0 + barGap + j * (barW + barGap)}
                    y={y}
                    width={barW}
                    height={PAD_T + plotH - y}
                    rx={Math.min(2, barW / 2)}
                    style={{ fill: s.color }}
                  >
                    <title>{`${label} · ${s.label}: ${v}`}</title>
                  </rect>
                );
              })}
              {i % labelStep === 0 ? (
                <text x={x0 + groupW / 2} y={H - 10} fontSize="9" textAnchor="middle" style={{ fill: 'var(--gps-muted)' }}>
                  {label.slice(5) /* MM-DD */}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {/* Legend. */}
      <div style={{ display: 'flex', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginTop: 'var(--gps-s-2)' }}>
        {series.map((s) => (
          <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--gps-muted)' }}>
            <span aria-hidden="true" style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color, display: 'inline-block' }} />
            {s.label}
          </span>
        ))}
      </div>
    </figure>
  );
}
